import { useEffect, useState, useCallback, useMemo } from 'react';
import Toast, { useToast } from '../../components/Toast';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { getVisitsByDateRange } from '../../lib/axiscare';
import { weeklyFlatPay } from '../../lib/payroll/payrollLines';
import { pullPayrollRun, saveRunTotals } from '../../lib/payroll/runs';
import { format } from 'date-fns';
import DataTable from '../../components/DataTable';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtH = (n) => `${Number(n || 0).toFixed(1)}h`;
const RATE_SUFFIX = { hourly: '/hr', weekly: '/wk', bi_weekly: '/2wk', monthly: '/mo', annually: '/yr', per_visit: '/visit' };
const fmtRate = (rate, payType) => rate != null ? `$${rate}${RATE_SUFFIX[payType] || '/hr'}` : null;
const PAY_TYPE_LABEL = { hourly: 'Hourly', weekly: 'Weekly', bi_weekly: 'Bi-weekly', monthly: 'Monthly', annually: 'Annual', per_visit: 'Per-visit' };
const PayTypeBadge = ({ payType }) => (
  <span style={{
    fontSize: 9, fontWeight: 600, borderRadius: 20, padding: '1px 6px', marginLeft: 6, textTransform: 'uppercase', letterSpacing: '0.03em',
    background: payType === 'hourly' ? 'var(--bg2)' : 'var(--accent-bg)',
    color: payType === 'hourly' ? 'var(--text3)' : 'var(--accent2)',
  }}>
    {PAY_TYPE_LABEL[payType] || 'Hourly'}
  </span>
);

export default function PayrollRunDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [run, setRun] = useState(null);
  const [lines, setLines] = useState([]);
  const [noGusto, setNoGusto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pulling, setPulling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const { toast, showToast, hideToast } = useToast(4000);
  const [tab, setTab] = useState('summary'); // 'summary' | 'breakdown'
  // Who the money goes to. The run always holds every line — this only
  // changes what you're looking at, so hours capture is never lost.
  const [payee, setPayee] = useState('all');  // 'all' | 'direct' | agency uuid
  const [expandedCaregivers, setExpandedCaregivers] = useState({});

  // Manual staff add (office staff with no AxisCare profile)
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [addingStaffId, setAddingStaffId] = useState(null);

  // Date range editing
  const [showEditDates, setShowEditDates] = useState(false);
  const [editFrom, setEditFrom] = useState('');
  const [editTo, setEditTo] = useState('');
  const [savingDates, setSavingDates] = useState(false);

  // Sorting (shared by summary + breakdown, both grouped by caregiver)
  const [sortKey, setSortKey] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);


  const loadRun = useCallback(async () => {
    const [runRes, linesRes] = await Promise.all([
      supabase.from('payroll_runs').select('*').eq('id', id).single(),
      supabase.from('payroll_run_lines').select('*').eq('run_id', id).order('caregiver_name').order('client_name'),
    ]);
    if (runRes.error) { console.error(runRes.error); setLoading(false); return; }
    setRun(runRes.data);
    setLines(linesRes.data || []);
    // Gusto is a follow-up, not an onboarding gate (2026-09-07) — so the one
    // place that must notice a person with no Gusto record is here, before
    // they work a pay period we cannot pay.
    const staffIds = [...new Set((linesRes.data || []).map(l => l.staff_id).filter(Boolean))];
    if (staffIds.length) {
      const { data: gustoRows } = await supabase.from('staff').select('id, full_name, gusto_created_at, hire_track, employment_type').in('id', staffIds);
      setNoGusto((gustoRows || []).filter(s => !s.gusto_created_at && (s.hire_track || s.employment_type) !== 'agency').map(s => s.full_name));
    } else {
      setNoGusto([]);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadRun(); }, [loadRun]);

  const updateRunTotals = (allLines) => saveRunTotals(supabase, id, allLines);

  // ── Manual staff add (no AxisCare profile) ────────────────────────────────
  const openAddStaff = async () => {
    setStaffSearch('');
    setShowAddStaff(true);
    const { data } = await supabase.from('staff').select('id, full_name, first_name, last_name, position_code, pay_rate, pay_type, employment_type, contracting_agency_id, contracting_agency:contracting_agencies(id, name)').eq('status', 'active').order('last_name');
    const alreadyIn = new Set(lines.map(l => l.staff_id).filter(Boolean));
    setStaffOptions((data || []).filter(s => !alreadyIn.has(s.id)));
  };

  const handleAddStaff = async (s) => {
    setAddingStaffId(s.id);
    try {
      const rate = s.pay_rate != null ? parseFloat(s.pay_rate) : null;
      const flat = weeklyFlatPay(rate, s.pay_type);
      const newLine = {
        run_id: id,
        staff_id: s.id,
        caregiver_axiscare_id: null,
        caregiver_profile_id: null,
        caregiver_name: s.full_name,
        caregiver_role: s.position_code,
        client_axiscare_id: null,
        client_name: null,
        client_ref: null,
        visit_count: 0,
        visit_dates: [],
        hours_raw: 0,
        hours_adjusted: 0,
        pay_rate: rate,
        pay_type: s.pay_type || 'hourly',
        pay_total: flat != null ? Math.round(flat * 100) / 100 : null,
        employment_type: s.employment_type ?? null,
        contracting_agency_id: s.contracting_agency_id ?? null,
        agency_name: s.contracting_agency?.name ?? null,
        is_edited: false,
        is_manual: true,
      };
      const { error } = await supabase.from('payroll_run_lines').insert([newLine]);
      if (error) throw error;
      await updateRunTotals([...lines, newLine]);
      setShowAddStaff(false);
      await loadRun();
      showToast(`Added ${s.full_name}.`);
    } catch (err) {
      showToast('Failed to add staff: ' + err.message, 'error');
    } finally {
      setAddingStaffId(null);
    }
  };

  const handleRemoveManualLine = async (line) => {
    if (!window.confirm(`Remove ${line.caregiver_name} from this run?`)) return;
    const { error } = await supabase.from('payroll_run_lines').delete().eq('id', line.id);
    if (error) { showToast('Failed to remove: ' + error.message, 'error'); return; }
    const remaining = lines.filter(l => l.id !== line.id);
    await updateRunTotals(remaining);
    await loadRun();
    showToast(`Removed ${line.caregiver_name}.`);
  };

  // ── Edit date range ───────────────────────────────────────────────────────
  const openEditDates = () => {
    setEditFrom(run.week_start);
    setEditTo(run.week_end);
    setShowEditDates(true);
  };

  const handleSaveDates = async () => {
    if (!editFrom || !editTo || editFrom > editTo) { showToast('Enter a valid date range.', 'error'); return; }
    setSavingDates(true);
    try {
      const { data: others } = await supabase.from('payroll_runs').select('id, week_start, week_end').neq('id', id);
      const overlap = (others || []).find(r => editFrom <= r.week_end && editTo >= r.week_start);
      if (overlap) {
        const label = `${format(new Date(overlap.week_start + 'T12:00:00'), 'MMM d')}–${format(new Date(overlap.week_end + 'T12:00:00'), 'MMM d, yyyy')}`;
        if (!window.confirm(`This range overlaps another run (${label}). Save anyway?`)) { setSavingDates(false); return; }
      }
      const { error } = await supabase.from('payroll_runs').update({ week_start: editFrom, week_end: editTo, updated_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
      setShowEditDates(false);
      await loadRun();
      showToast('Dates updated. Re-pull from AxisCare to load visits for the new range.');
    } catch (err) {
      showToast('Failed to update dates: ' + err.message, 'error');
    } finally {
      setSavingDates(false);
    }
  };

  // ── Pull from AxisCare ────────────────────────────────────────────────────
  const handlePull = async () => {
    if (!run) return;
    if (lines.length > 0) {
      const editedNote = editedCount > 0 ? ` ${editedCount} manually edited line${editedCount > 1 ? 's' : ''} will be lost.` : '';
      if (!window.confirm(`Re-pulling will replace all ${lines.length} existing line items for this run.${editedNote}\n\nContinue?`)) return;
    }
    setPulling(true);
    try {
      const { data: visits, error } = await getVisitsByDateRange(run.week_start, run.week_end, { mode: 'payroll' });
      if (error) throw new Error(error.message || 'AxisCare fetch failed');
      const raw = visits?.results?.visits || [];
      const { lines: newLines, exceptions, autoSalaried } = await pullPayrollRun(supabase, run, raw);
      await loadRun();
      const pulledStaff = new Set(newLines.map(l => l.staff_id || l.caregiver_axiscare_id || l.caregiver_name)).size;
      const ambiguousNote = exceptions.ambiguous.length > 0 ? ` ⚠ ${exceptions.ambiguous.length} name${exceptions.ambiguous.length > 1 ? 's' : ''} matched multiple staff records and were left unmatched: ${exceptions.ambiguous.join(', ')}.` : '';
      const autoNote = autoSalaried.length > 0 ? ` Auto-included ${autoSalaried.length} office staff on fixed pay.` : '';
      const unclockedNote = exceptions.unpaid.length > 0 ? ` ${exceptions.unpaid.length} visit${exceptions.unpaid.length > 1 ? 's' : ''} had no usable clock record and could not be paid.` : '';
      showToast(`Pulled ${newLines.length} line items for ${pulledStaff} staff from AxisCare.${autoNote}${unclockedNote}${ambiguousNote}`, exceptions.ambiguous.length > 0 ? 'error' : 'success', exceptions.ambiguous.length > 0 ? 8000 : 4000);
    } catch (err) {
      console.error('Pull error:', err);
      showToast('Failed to pull from AxisCare: ' + err.message, 'error');
    } finally {
      setPulling(false);
    }
  };

  // ── Save edits ────────────────────────────────────────────────────────────
  const handleLineChange = (lineId, newHours) => {
    setLines(prev => prev.map(l => {
      if (l.id !== lineId) return l;
      const parsed = parseFloat(newHours);
      const adj = isNaN(parsed) || parsed < 0 ? 0 : parsed;
      // Only hourly pay recalculates from hours — per-visit and salaried
      // types are unaffected by an hours edit (pay isn't hours-based).
      const pay = l.pay_type === 'hourly' && l.pay_rate != null
        ? Math.round(adj * l.pay_rate * 100) / 100
        : l.pay_total;
      return { ...l, hours_adjusted: adj, pay_total: pay, is_edited: adj !== l.hours_raw };
    }));
  };

  // Writes every line's hours and pay as they stand on screen, then the run
  // totals. Throws on the first failure so nothing reports success it did not
  // earn. Shared by Save and Finalize: finalizing used to leave unsaved hour
  // edits on screen and lock the run underneath them.
  const persistLines = async () => {
    const updates = lines.map(l => supabase.from('payroll_run_lines').update({
      hours_adjusted: l.hours_adjusted,
      pay_total: l.pay_total,
      is_edited: l.is_edited,
      updated_at: new Date().toISOString(),
    }).eq('id', l.id));
    const results = await Promise.all(updates);
    const failed = results.filter(r => r.error);
    if (failed.length > 0) {
      throw new Error(`${failed.length} of ${lines.length} line${lines.length > 1 ? 's' : ''} failed to save: ${failed[0].error.message}`);
    }
    await updateRunTotals(lines);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistLines();
      await loadRun();
      showToast('Changes saved.');
    } catch (err) {
      showToast('Save failed: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Finalize ──────────────────────────────────────────────────────────────
  // supabase-js does not throw on a failed update; it returns { error }. This
  // used to ignore that and announce "finalized" whatever happened.
  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await persistLines();
      const { error } = await supabase.from('payroll_runs').update({
        status: 'finalized',
        finalized_at: new Date().toISOString(),
        finalized_by_name: profile?.full_name,
        updated_at: new Date().toISOString(),
      }).eq('id', id).eq('status', 'draft');
      if (error) throw error;
      setShowFinalizeModal(false);
      await loadRun();
      showToast('Payroll run finalized.');
    } catch (err) {
      showToast('Finalize failed: ' + err.message, 'error');
    } finally {
      setFinalizing(false);
    }
  };

  const handleReopen = async () => {
    const { error } = await supabase.from('payroll_runs')
      .update({ status: 'draft', finalized_at: null, finalized_by_name: null, updated_at: new Date().toISOString() })
      .eq('id', id).eq('status', 'finalized');
    if (error) { showToast('Could not re-open: ' + error.message, 'error'); return; }
    await loadRun();
    showToast('Run re-opened as draft.');
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (!run) return;
    const headers = ['Caregiver', 'Role', 'Paid To', 'Employment', 'Client', 'Client Ref', 'Visits', 'Visit Dates', 'Hours (AxisCare)', 'Hours (Adjusted)', 'Rate', 'Total', 'Edited'];
    const rows = viewLines.map(l => [
      l.caregiver_name, l.caregiver_role || '',
      l.agency_name || 'Direct', l.employment_type || '',
      l.client_name || '', l.client_ref || '',
      l.visit_count, (l.visit_dates || []).join('; '),
      l.hours_raw, l.hours_adjusted,
      l.pay_rate != null ? l.pay_rate : '',
      l.pay_total != null ? l.pay_total : '',
      l.is_edited ? 'Yes' : 'No',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    const scope = payee === 'all' ? '' : payee === 'direct' ? '_direct' : `_${(agencyGroups.find(a => a.id === payee)?.name || 'agency').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
    a.download = `payroll_${run.week_start}_${run.week_end}${scope}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Computed values ───────────────────────────────────────────────────────
  const editedCount = lines.filter(l => l.is_edited).length;
  const missingRate = [...new Set(lines.filter(l => l.pay_rate == null).map(l => l.caregiver_name))];
  // What the last pull could not pay, or paid twice. Saved on the run since
  // 2026-10-28 — the Monday job pulls with nobody watching, and before that
  // this list lived only in the browser of whoever pressed Pull.
  const unclockedVisits = run?.pull_exceptions?.unpaid || [];
  const overlaps = run?.pull_exceptions?.overlaps || [];
  // Visits the pull left unpaid because their line is not paid per visit —
  // a count on older runs, a list from 2026-10-29.
  const notPayable = Array.isArray(run?.pull_exceptions?.notPayable) ? run.pull_exceptions.notPayable : [];
  const totalHours = lines.reduce((s, l) => s + (l.hours_adjusted || 0), 0);
  const totalPay = lines.reduce((s, l) => s + (l.pay_total || 0), 0);
  const staffCount = new Set(lines.map(l => l.staff_id || l.caregiver_axiscare_id || l.caregiver_name)).size;

  // Money owed to an agency on an invoice, not paid to a person. Read off the
  // line rather than the staff record so a closed run keeps the attribution it
  // was pulled with.
  const isAgencyLine = (l) => !!l.contracting_agency_id;
  const agencyLines = lines.filter(isAgencyLine);
  const agencyPay   = agencyLines.reduce((t, l) => t + (l.pay_total || 0), 0);
  const directPay   = totalPay - agencyPay;

  // Per-agency subtotals — this is what an agency invoice gets checked against.
  const agencyGroups = Object.values(agencyLines.reduce((acc, l) => {
    const k = l.contracting_agency_id;
    acc[k] = acc[k] || { id: k, name: l.agency_name || 'Unnamed agency', hours: 0, pay: 0, visits: 0, people: new Set() };
    acc[k].hours += l.hours_adjusted || 0;
    acc[k].pay   += l.pay_total || 0;
    acc[k].visits += l.visit_count || 0;
    acc[k].people.add(l.staff_id || l.caregiver_axiscare_id || l.caregiver_name);
    return acc;
  }, {})).sort((a, b) => b.pay - a.pay);

  const viewLines =
    payee === 'all'    ? lines
    : payee === 'direct' ? lines.filter(l => !isAgencyLine(l))
    : lines.filter(l => l.contracting_agency_id === payee);
  const viewHours = viewLines.reduce((t, l) => t + (l.hours_adjusted || 0), 0);
  const viewPay   = viewLines.reduce((t, l) => t + (l.pay_total || 0), 0);

  // Group lines by caregiver — keyed by staff/AxisCare ID, not name, so two
  // staff who happen to share a name don't get merged into one row.
  const groupByCaregiver = (ls) => {
    const map = {};
    for (const l of ls) {
      const cgKey = l.staff_id || l.caregiver_axiscare_id || l.caregiver_name;
      if (!map[cgKey]) {
        map[cgKey] = { key: cgKey, name: l.caregiver_name, role: l.caregiver_role, payRate: l.pay_rate, payType: l.pay_type || 'hourly', hours: 0, pay: 0, visits: 0, edited: false, clients: [], cgNotes: l.notes || '', agencyName: l.agency_name || null };
      }
      map[cgKey].hours += l.hours_adjusted || 0;
      map[cgKey].pay += l.pay_total || 0;
      map[cgKey].visits += l.visit_count || 0;
      if (l.is_edited) map[cgKey].edited = true;
      map[cgKey].clients.push(l);
    }
    return Object.values(map);
  };
  // Overtime is a run-level compliance warning, so it looks at every line no
  // matter which payee view is on screen.
  // Salaried staff are paid a fixed amount whatever they clock; the 40h
  // banner is about hourly pay.
  const overtimeStaff = groupByCaregiver(lines).filter(cg => cg.hours > 40 && cg.payType === 'hourly');
  const byCaregiver = groupByCaregiver(viewLines).sort((a, b) => {
    const av = a[sortKey]; const bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1; if (bv == null) return -1;
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortAsc ? cmp : -cmp;
  });
  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc(p => !p);
    else { setSortKey(key); setSortAsc(true); }
  };
  const SortArrow = ({ field }) => (
    <span style={{ marginLeft: 3, fontSize: 9, opacity: sortKey === field ? 1 : 0.3 }}>
      {sortKey === field ? (sortAsc ? '↑' : '↓') : '⇅'}
    </span>
  );

  // Controlled-sorting bridge for the summary tab's DataTable — shares the
  // same sortKey/sortAsc state as the breakdown tab's manual sort headers.
  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: !sortAsc }] : [], [sortKey, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortAsc(!next[0].desc);
  };

  const summaryColumns = [
    {
      id: 'name', header: 'Caregiver', size: 190,
      meta: { mobile: 'primary' },
      cell: info => { const cg = info.row.original; return <>{cg.name}<PayTypeBadge payType={cg.payType} /></>; },
    },
    { id: 'role', header: 'Role', size: 110, meta: { mobile: 'secondary' }, cell: info => <span style={{ color: 'var(--text3)', fontSize: 12 }}>{info.row.original.role || '—'}</span> },
    {
      id: 'payee', header: 'Paid to', size: 150, meta: { mobile: 'hidden' },
      cell: info => {
        const cg = info.row.original;
        return cg.agencyName
          ? <span title="Billed by the agency — not paid to this person" style={{ fontSize: 10, background: 'var(--info-bg)', color: 'var(--info)', padding: '2px 7px', borderRadius: 20, fontWeight: 500 }}>{cg.agencyName}</span>
          : <span style={{ fontSize: 12, color: 'var(--text3)' }}>Direct</span>;
      },
    },
    { id: 'visits', header: 'Visits', size: 80, meta: { mobile: 'secondary' }, cell: info => <span style={{ color: 'var(--text2)' }}>{info.row.original.visits}</span> },
    {
      id: 'hours_raw', header: 'Hours (raw)', size: 110, enableSorting: false, meta: { mobile: 'hidden' },
      cell: info => <span style={{ color: 'var(--text3)' }}>{fmtH(info.row.original.clients.reduce((s, l) => s + (l.hours_raw || 0), 0))}</span>,
    },
    {
      id: 'hours', header: 'Hours (adj.)', size: 110, meta: { mobile: 'primary' },
      cell: info => { const cg = info.row.original; return <span style={{ fontWeight: 500, color: cg.hours > 40 && cg.payType === 'hourly' ? 'var(--danger)' : 'var(--text2)' }}>{fmtH(cg.hours)}</span>; },
    },
    {
      id: 'ot', header: 'OT', size: 100, enableSorting: false, meta: { mobile: 'hidden' },
      cell: info => {
        const cg = info.row.original;
        return cg.hours > 40 && cg.payType === 'hourly'
          ? <span style={{ fontSize: 10, background: 'var(--danger-bg)', color: 'var(--danger)', padding: '2px 6px', borderRadius: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>⚠ +{fmtH(cg.hours - 40)}</span>
          : <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>;
      },
    },
    {
      id: 'payRate', header: 'Pay rate', size: 120, meta: { mobile: 'secondary' },
      cell: info => { const cg = info.row.original; return cg.payRate != null ? <span style={{ color: 'var(--text3)' }}>{fmtRate(cg.payRate, cg.payType)}</span> : <span style={{ color: 'var(--danger)', fontSize: 11 }}>⚠ Not set</span>; },
    },
    {
      id: 'pay', header: 'Total pay', size: 110, meta: { mobile: 'primary' },
      cell: info => { const cg = info.row.original; return <span style={{ fontWeight: 500 }}>{cg.payRate != null ? fmt$(cg.pay) : '—'}</span>; },
    },
    {
      id: 'notes', header: 'Notes', size: 160, enableSorting: false, meta: { mobile: 'hidden' },
      cell: info => {
        const cg = info.row.original;
        if (isFinalized) return <span style={{ fontSize: 11, color: 'var(--text3)' }}>{cg.cgNotes || '—'}</span>;
        return (
          <input
            type="text"
            placeholder="Add note…"
            defaultValue={cg.cgNotes}
            onClick={e => e.stopPropagation()}
            onBlur={async e => {
              const val = e.target.value.trim();
              let q = supabase.from('payroll_run_lines').update({ notes: val || null }).eq('run_id', id);
              const first = cg.clients[0];
              q = first?.staff_id ? q.eq('staff_id', first.staff_id)
                : first?.caregiver_axiscare_id ? q.eq('caregiver_axiscare_id', first.caregiver_axiscare_id)
                : q.eq('caregiver_name', cg.name);
              const { error } = await q;
              if (error) { showToast('Note not saved: ' + error.message, 'error'); return; }
              // Keep the in-memory lines in step so a re-render does not
              // show the old note under the one just typed.
              setLines(prev => prev.map(l => cg.clients.some(c => c.id === l.id) ? { ...l, notes: val || null } : l));
            }}
            style={{ width: 130, border: '0.5px solid var(--border)', borderRadius: 5, padding: '3px 6px', fontSize: 11, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', outline: 'none' }}
          />
        );
      },
    },
    {
      id: 'editedFlag', header: '', size: 80, enableSorting: false, meta: { mobile: 'hidden' },
      cell: info => info.row.original.edited ? <span style={{ fontSize: 10, background: 'var(--warn-bg)', color: 'var(--warn)', borderRadius: 20, padding: '2px 6px', fontWeight: 500 }}>edited</span> : null,
    },
  ];

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;
  if (!run) return <div className="empty-state">Run not found.</div>;

  const isFinalized = run.status === 'finalized';
  const weekLabel = `${format(new Date(run.week_start + 'T12:00:00'), 'MMM d')}–${format(new Date(run.week_end + 'T12:00:00'), 'MMM d, yyyy')}`;

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <button onClick={() => navigate('/finance/payroll')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 13, fontFamily: 'inherit', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          ← Payroll Mgmt.
        </button>
        <span style={{ color: 'var(--text3)' }}>/</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text2)' }}>{weekLabel}</span>
        {!isFinalized && (
          <button onClick={openEditDates} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent2)', fontSize: 11, fontFamily: 'inherit', padding: 0 }}>
            ✎ Edit dates
          </button>
        )}
        <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
          background: isFinalized ? 'var(--success-bg)' : 'var(--warn-bg)',
          color: isFinalized ? 'var(--success)' : 'var(--warn)' }}>
          {isFinalized ? '✓ Finalized' : '✎ Draft'}
        </span>
      </div>

      {/* Finalized banner */}
      {isFinalized && (
        <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>✓ Finalized by <strong>{run.finalized_by_name}</strong> on {format(new Date(run.finalized_at), 'MMM d, yyyy')}</span>
          {profile?.role === 'admin' && (
            <button onClick={handleReopen} style={{ fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--success-border)', background: 'transparent', color: 'var(--success)', borderRadius: 6, padding: '3px 10px' }}>
              Re-open (admin)
            </button>
          )}
        </div>
      )}

      {/* Overtime warning */}
      {overtimeStaff.length > 0 && (
        <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: 'var(--danger)' }}>
          ⚠ <strong>{overtimeStaff.length} caregiver{overtimeStaff.length > 1 ? 's' : ''} exceeded 40h this week</strong> — verify OT pay applies:{' '}
          {overtimeStaff.map(cg => `${cg.name} (${fmtH(cg.hours)})`).join(', ')}
        </div>
      )}

      {/* No Gusto record — they cannot be paid until the office creates it */}
      {noGusto.length > 0 && (
        <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>
          ⚠ {noGusto.length} {noGusto.length > 1 ? 'people' : 'person'} on this run {noGusto.length > 1 ? 'have' : 'has'} no Gusto payroll record yet: <strong>{noGusto.join(', ')}</strong>. Create them in Gusto and mark done on{' '}
          <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate('/hr/training-compliance')}>Training &amp; Compliance › Onboarding follow-ups</span>.
        </div>
      )}

      {/* Worked but never clocked — nothing to pay until someone records the
          clock. Unlike a missing rate this is not a data-entry gap in CAERA,
          it is a caregiver who did not clock, so it names the visit and day. */}
      {/* Same hours clocked twice — two clients at once, or one visit entered
          twice. Both are paid as they stand, so this is the check before
          finalizing, not a correction the pull makes. */}
      {overlaps.length > 0 && (
        <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--warn)' }}>
          ⚠ <strong>{overlaps.length}</strong> clocked {overlaps.length > 1 ? 'visits overlap' : 'visit overlaps'} another for the same caregiver, so those hours are paid twice:{' '}
          {overlaps.slice(0, 6).map(o => `${o.name} · ${o.date} (${o.clients.join(' / ')}, ${o.hours}h)`).join(', ')}
          {overlaps.length > 6 && ` and ${overlaps.length - 6} more`}.
          {' '}Check the clock times in AxisCare, then pull again.
        </div>
      )}

      {notPayable.length > 0 && (
        <div style={{ background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--text2)' }}>
          {notPayable.length} {notPayable.length > 1 ? 'visits were' : 'visit was'} not paid because the service is not paid per visit:{' '}
          {notPayable.slice(0, 6).map(n => `${n.name} · ${n.date} (${n.client}${n.service ? `, ${n.service}` : ''})`).join(', ')}
          {notPayable.length > 6 && ` and ${notPayable.length - 6} more`}.
          {' '}If any of these is ordinary care, correct its service in AxisCare and pull again.
        </div>
      )}

      {unclockedVisits.length > 0 && (
        <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--warn)' }}>
          ⚠ <strong>{unclockedVisits.length}</strong> {unclockedVisits.length > 1 ? 'visits were' : 'visit was'} not paid — no usable clock record:{' '}
          {unclockedVisits.slice(0, 6).map(u => `${u.name} · ${u.date}${u.reason && u.reason !== 'no clock-in/out' ? ` (${u.reason})` : ''}`).join(', ')}
          {unclockedVisits.length > 6 && ` and ${unclockedVisits.length - 6} more`}.
          {' '}Correct the clock times on the visit, then pull again.
        </div>
      )}

      {/* Missing rate warning */}
      {missingRate.length > 0 && (
        <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--warn)' }}>
          ⚠ {missingRate.length} caregiver{missingRate.length > 1 ? 's' : ''} have no pay rate set — their totals are excluded: <strong>{missingRate.join(', ')}</strong>.{' '}
          <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate('/hr/staff-records')}>Go to Staff Records →</span>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'Total payroll',    value: fmt$(totalPay),       sub: agencyPay > 0 ? `${fmt$(directPay)} direct · ${fmt$(agencyPay)} agency` : `${staffCount} caregivers` },
          { label: 'Total hours',      value: fmtH(totalHours),     sub: `${lines.reduce((s,l) => s + (l.visit_count||0), 0)} visits` },
          { label: 'Overtime staff',   value: overtimeStaff.length, sub: overtimeStaff.length > 0 ? '>40h this week' : 'No OT', warn: overtimeStaff.length > 0 },
          { label: 'Manual edits',     value: editedCount,          sub: editedCount > 0 ? 'rows adjusted' : 'no changes', warn: editedCount > 0 },
          { label: 'Missing pay rate', value: missingRate.length,   sub: missingRate.length > 0 ? 'staff excluded' : 'all rates set', warn: missingRate.length > 0 },
        ].map(s => (
          <div key={s.label} style={{ background: s.warn && s.value > 0 ? 'var(--warn-bg)' : 'var(--accent-bg)', border: `0.5px solid ${s.warn && s.value > 0 ? 'var(--warn-border)' : 'var(--accent-border)'}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: s.warn && s.value > 0 ? 'var(--warn)' : 'var(--accent2)', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: s.warn && s.value > 0 ? 'var(--warn)' : 'var(--accent2)' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Main card */}
      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['summary', 'By caregiver'], ['breakdown', 'By caregiver + client']].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: tab === k ? 'var(--accent)' : 'transparent', color: tab === k ? '#fff' : 'var(--text3)', borderColor: tab === k ? 'var(--accent)' : 'var(--border)' }}>{label}</button>
            ))}
            {agencyGroups.length > 0 && (
              <select
                value={payee}
                onChange={e => setPayee(e.target.value)}
                title="Filter by who the money is paid to. Every line stays in the run either way."
                style={{ fontSize: 12, padding: '4px 10px', borderRadius: 20, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontFamily: 'inherit', marginLeft: 4 }}
              >
                <option value="all">Everyone</option>
                <option value="direct">Direct pay only</option>
                {agencyGroups.map(a => <option key={a.id} value={a.id}>{a.name} (payable)</option>)}
              </select>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            {!isFinalized && (
              <button onClick={handlePull} disabled={pulling} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: pulling ? 'default' : 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--text2)', opacity: pulling ? 0.6 : 1 }}>
                {pulling ? 'Pulling…' : '↻ Pull from AxisCare'}
              </button>
            )}
            {!isFinalized && (
              <button onClick={openAddStaff} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--text2)' }}>
                + Add staff
              </button>
            )}
            <button onClick={handleExportCSV} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--text2)' }}>
              ↓ Export CSV
            </button>
            {!isFinalized && (
              <>
                <button onClick={handleSave} disabled={saving} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--accent-border)', background: 'var(--accent-bg)', color: 'var(--accent2)', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving…' : 'Save edits'}
                </button>
                <button onClick={() => setShowFinalizeModal(true)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--success)', color: '#fff' }}>
                  🔒 Finalize run
                </button>
              </>
            )}
          </div>
        </div>

        {agencyGroups.length > 0 && (
          <div style={{ borderBottom: '0.5px solid var(--border)', padding: '10px 16px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', background: 'var(--bg2)' }}>
            <span style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)' }}>Agency payable</span>
            {agencyGroups.map(a => (
              <button
                key={a.id}
                onClick={() => setPayee(payee === a.id ? 'all' : a.id)}
                title="Check this against the agency's invoice for the week. Hours come from AxisCare clock-ins."
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 8, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                  border: `0.5px solid ${payee === a.id ? 'var(--info)' : 'var(--border)'}`,
                  background: payee === a.id ? 'var(--info-bg)' : 'var(--bg)',
                  borderRadius: 8, padding: '5px 12px',
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>{a.name}</span>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{a.people.size} staff · {fmtH(a.hours)}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--info)' }}>{fmt$(a.pay)}</span>
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>
              Direct pay {fmt$(directPay)}
            </span>
          </div>
        )}

        {lines.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 0' }}>
            No data yet.{!isFinalized && <> Click <strong>"Pull from AxisCare"</strong> to load this week's visits.</>}
          </div>
        ) : tab === 'summary' ? (
          // ── Summary tab ─────────────────────────────────────────────────
          <>
            <DataTable
              tableId="payroll-run-summary"
              columns={summaryColumns}
              data={byCaregiver}
              getRowId={cg => cg.key}
              rowStyle={cg => cg.hours > 40 && cg.payType === 'hourly' ? { background: 'rgba(229,62,62,0.03)' } : null}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              manualSorting
              maxHeight="calc(100vh - 420px)"
            />
            <div style={{ borderTop: '1px solid var(--border)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--text3)' }}>
                {payee === 'all' ? 'Totals' : payee === 'direct' ? 'Direct pay' : `${agencyGroups.find(a => a.id === payee)?.name || 'Agency'} payable`} — {fmtH(viewHours)}
              </span>
              <span style={{ fontWeight: 600, color: 'var(--accent2)' }}>{fmt$(viewPay)}</span>
            </div>
          </>
        ) : (
          // ── Breakdown tab ────────────────────────────────────────────────
          <div className="table-wrapper">
            <table>
              <thead><tr>
                <th style={{ color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>Caregiver / Client<SortArrow field="name" /></th>
                <th style={{ color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('role')}>Role<SortArrow field="role" /></th>
                <th style={{ color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('visits')}>Visits<SortArrow field="visits" /></th>
                <th style={{ color: 'var(--text3)' }}>Visit dates</th>
                <th style={{ color: 'var(--text3)' }}>Hrs (raw)</th>
                <th style={{ color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('hours')}>Hrs (adj.)<SortArrow field="hours" /></th>
                <th style={{ color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('payRate')}>Pay rate<SortArrow field="payRate" /></th>
                <th style={{ color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('pay')}>Subtotal<SortArrow field="pay" /></th>
                <th style={{ width: 60 }}></th>
              </tr></thead>
              <tbody>
                {byCaregiver.map(cg => {
                  const isOpen = expandedCaregivers[cg.key] !== false; // default open
                  return [
                    // Caregiver header row
                    <tr key={`hdr-${cg.key}`} style={{ background: 'var(--accent-bg)', cursor: 'pointer' }} onClick={() => setExpandedCaregivers(p => ({ ...p, [cg.key]: !isOpen }))}>
                      <td colSpan={2} style={{ fontWeight: 500, fontSize: 13 }}>
                        <span style={{ marginRight: 6, fontSize: 10, color: 'var(--text3)' }}>{isOpen ? '▼' : '▶'}</span>
                        {cg.name}
                        <PayTypeBadge payType={cg.payType} />
                        {cg.edited && <span style={{ fontSize: 10, background: 'var(--warn-bg)', color: 'var(--warn)', borderRadius: 20, padding: '2px 6px', fontWeight: 500, marginLeft: 8 }}>edited</span>}
                      </td>
                      <td style={{ color: 'var(--text3)', fontSize: 12 }}>{cg.visits}</td>
                      <td></td>
                      <td style={{ color: 'var(--text3)' }}>{fmtH(cg.clients.reduce((s,l) => s + (l.hours_raw||0), 0))}</td>
                      <td style={{ fontWeight: 500 }}>{fmtH(cg.hours)}</td>
                      <td style={{ color: 'var(--text3)' }}>{cg.payRate != null ? fmtRate(cg.payRate, cg.payType) : <span style={{ color: 'var(--danger)', fontSize: 11 }}>⚠ Not set</span>}</td>
                      <td style={{ fontWeight: 600, color: 'var(--accent2)' }}>{cg.payRate != null ? fmt$(cg.pay) : '—'}</td>
                      <td></td>
                    </tr>,
                    // Client detail rows
                    ...(isOpen ? cg.clients.map(l => (
                      <tr key={l.id} style={{ background: 'var(--bg)' }}>
                        <td style={{ paddingLeft: 28, color: 'var(--text2)', fontSize: 12 }}>
                          {l.client_ref && <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)', marginRight: 6 }}>{l.client_ref}</span>}
                          {l.client_name || (l.staff_id ? <em style={{ color: 'var(--text3)' }}>Fixed pay — no client</em> : '—')}
                        </td>
                        <td style={{ color: 'var(--text3)', fontSize: 11 }}></td>
                        <td style={{ color: 'var(--text3)', fontSize: 12 }}>{l.visit_count}</td>
                        <td style={{ fontSize: 11, color: 'var(--text3)', maxWidth: 180 }}>
                          {(l.visit_dates || []).length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                              {(l.visit_dates || []).map(d => (
                                <span key={d} style={{ background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', borderRadius: 4, padding: '1px 5px', fontSize: 10, color: 'var(--accent2)', whiteSpace: 'nowrap' }}>
                                  {format(new Date(d + 'T12:00:00'), 'MMM d')}
                                </span>
                              ))}
                            </div>
                          ) : '—'}
                        </td>
                        <td style={{ color: 'var(--text3)', fontSize: 12 }}>{fmtH(l.hours_raw)}</td>
                        <td>
                          {!isFinalized ? (
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              value={l.hours_adjusted}
                              onChange={e => handleLineChange(l.id, e.target.value)}
                              style={{ width: 64, border: '1px solid var(--border)', borderRadius: 5, padding: '3px 6px', fontSize: 12, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', outline: 'none' }}
                            />
                          ) : (
                            <span style={{ fontSize: 12 }}>{fmtH(l.hours_adjusted)}</span>
                          )}
                        </td>
                        <td style={{ color: 'var(--text3)', fontSize: 12 }}>{l.pay_rate != null ? fmtRate(l.pay_rate, l.pay_type) : '—'}</td>
                        <td style={{ fontSize: 12 }}>{l.pay_total != null ? fmt$(l.pay_total) : '—'}</td>
                        <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {l.is_edited && <span style={{ fontSize: 10, color: 'var(--warn)' }}>● edited</span>}
                          {l.is_manual && !isFinalized && (
                            <button onClick={() => handleRemoveManualLine(l)} title="Remove from run" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 12, padding: 0 }}>✕</button>
                          )}
                        </td>
                      </tr>
                    )) : []),
                  ];
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '1px solid var(--border)' }}>
                  <td colSpan={5} style={{ fontWeight: 500, fontSize: 12, color: 'var(--text3)', padding: '10px 12px' }}>Totals</td>
                  <td style={{ fontWeight: 500 }}>{fmtH(totalHours)}</td>
                  <td></td>
                  <td style={{ fontWeight: 600, color: 'var(--accent2)' }}>{fmt$(totalPay)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Edit dates modal */}
      {showEditDates && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowEditDates(false)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 420, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Edit run dates</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Changing dates doesn't move existing line items — re-pull from AxisCare afterward to load visits for the new range.</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>From</div>
                <input type="date" value={editFrom} onChange={e => setEditFrom(e.target.value)}
                  style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>To</div>
                <input type="date" value={editTo} onChange={e => setEditTo(e.target.value)}
                  style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowEditDates(false)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={handleSaveDates} disabled={savingDates} style={{ flex: 2, padding: '10px 0', background: savingDates ? 'var(--border2)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: savingDates ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {savingDates ? 'Saving…' : 'Save dates'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add staff modal */}
      {showAddStaff && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowAddStaff(false)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 440, maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Add staff to this run</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
              For staff without an AxisCare caregiver profile (e.g. office staff on a fixed rate). Weekly/monthly/etc. pay is applied immediately; hourly pay can be entered afterward in the breakdown tab.
            </div>
            <input
              type="text" placeholder="Search staff…" value={staffSearch} onChange={e => setStaffSearch(e.target.value)}
              style={{ border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', marginBottom: 12 }}
            />
            <div style={{ overflowY: 'auto', flex: 1, border: '0.5px solid var(--border)', borderRadius: 8 }}>
              {staffOptions.filter(s => s.full_name.toLowerCase().includes(staffSearch.toLowerCase())).length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>No matching staff (or everyone's already on this run).</div>
              ) : staffOptions.filter(s => s.full_name.toLowerCase().includes(staffSearch.toLowerCase())).map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '0.5px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.position_code || '—'} · {s.pay_rate != null ? fmtRate(parseFloat(s.pay_rate), s.pay_type) : 'no rate set'}</div>
                  </div>
                  <button
                    onClick={() => handleAddStaff(s)}
                    disabled={addingStaffId === s.id}
                    style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: addingStaffId === s.id ? 'default' : 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--accent)', color: '#fff', opacity: addingStaffId === s.id ? 0.6 : 1 }}
                  >
                    {addingStaffId === s.id ? 'Adding…' : '+ Add'}
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setShowAddStaff(false)} style={{ marginTop: 16, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
          </div>
        </div>
      )}

      {/* Finalize modal */}
      {showFinalizeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowFinalizeModal(false)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 420, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Finalize payroll run</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              {weekLabel} · {staffCount} staff · {fmtH(totalHours)} · {fmt$(totalPay)} total
            </div>
            <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 12px', marginBottom: 20, fontSize: 12, color: 'var(--warn)' }}>
              ⚠ This locks the run. Future edits require admin re-open.
              {editedCount > 0 && <> {editedCount} line{editedCount > 1 ? 's were' : ' was'} manually adjusted from AxisCare data.</>}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowFinalizeModal(false)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={handleFinalize} disabled={finalizing} style={{ flex: 2, padding: '10px 0', background: finalizing ? 'var(--border2)' : 'var(--success)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: finalizing ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {finalizing ? 'Finalizing…' : '🔒 Confirm & finalize'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast {...(toast || {})} onClose={hideToast} />
    </div>
  );
}
